import { useId } from "react";
import { useSelectedEntity } from "@/hooks/useSelectedEntity";
import "./EntitySwitcher.css";

interface EntitySwitcherProps {
  /** `light` sits on a white/canvas surface (`AppShell.tsx`'s entity bar); `dark` sits
   *  on the horizon's deep-green gradient (`Dashboard.tsx`'s header) — same on-dark
   *  contrast pairing `SetupMenu.tsx`'s own `variant` prop already uses. */
  variant?: "light" | "dark";
}

/**
 * ef-047-1 — the ONE control that drives every entity-scoped screen (Fields, Grain,
 * Livestock) plus Home's horizon row. Same `<label>` + native `<select>` shape
 * `ReportsScheduleF.tsx`'s own "Entity" picker already uses — role `combobox`,
 * accessible name "Entity" (HTML-AAM's implicit mapping for a single-select
 * `<select>`), never a raw CSS selector for a tester to key off.
 *
 * Self-contained: reads and writes `useSelectedEntity()` itself rather than taking
 * `entityId`/`onChange` as props, so every mount point (`AppShell.tsx`, `Dashboard.tsx`)
 * is a one-line `<EntitySwitcher />` with nothing to wire — the module-scoped bus inside
 * the hook is what keeps every mounted instance (and every consumer of the hook) in
 * sync, not prop plumbing.
 */
export function EntitySwitcher({ variant = "light" }: EntitySwitcherProps) {
  const { entities, entityId, status, select } = useSelectedEntity();
  const fieldId = useId();

  return (
    <div className={`entity-switcher entity-switcher--${variant}`}>
      <label className="entity-switcher__label" htmlFor={fieldId}>
        Entity
      </label>
      <select
        id={fieldId}
        className="entity-switcher__select"
        value={entityId ?? ""}
        disabled={status !== "ready" || entities.length === 0}
        aria-busy={status === "loading"}
        data-state={status === "loading" ? "loading" : entities.length === 0 ? "empty" : "ready"}
        onChange={(event) => select(event.target.value)}
      >
        {/* ef-047-1 gate 29 fix — NEVER a placeholder <option> while loading: a
            "Loading…" entry used to be the first (and only) option, so a tester
            reading option labels before the fetch settled read the placeholder as
            the default entity. Loading now renders zero options (disabled select,
            `aria-busy`/`data-state="loading"` still expose the state to a tester
            without lying about what's selected) so the first option to ever exist
            is always a real entity, default first. */}
        {status === "loading" ? null : entities.length === 0 ? (
          <option value="">No entities</option>
        ) : (
          entities.map((entity) => (
            <option key={entity.id} value={entity.id}>
              {entity.name}
            </option>
          ))
        )}
      </select>
    </div>
  );
}
